/**
 * Merging one plant into another (D66): what a merge would move, and the merge itself.
 *
 * The server decides everything here (bloom-backend services/plants/plantMerge):
 * which two plants may merge (the same crop in the same bed), which variety the
 * kept plant has by default, what moves and how it is worded in the reader's
 * language. The app draws what it is sent and works out nothing of its own.
 *
 * Endpoints:
 * - GET  /plants/:plantObjId/merge-preview?into=<id>&lang= → MergePreview (the
 *   plant that goes is the path's, the one that stays is `into`).
 * - POST /plants/:plantObjId/merge { intoPlantObjId, varietyId } → MergeResult.
 * A refusal answers 400, 404 or 409 with `{ success: false, code, message }`; a
 * failure on our side answers 500 (reported to Sentry by the server).
 *
 * Who uses it: features/plant-list MergePlantSheet. Caching: the preview is
 * read under ['merge-preview', gone, keep] (lib/mergePlants previewKey).
 * Auth: the signed-in token; writes land in the garden being tended.
 *
 * @module api/plantMerge
 */

import { apiClient } from './apiClient';
import { getLanguage } from '@/shared/i18n/i18nSetup';

/** One of the two plants in a merge preview. */
export interface MergePreviewPlant {
  /** The plant's id. */
  plantObjId: number;
  /** Its name as the server says it, telling the pair apart. */
  name: string;
  /** Its variety; a placeholder is "Not sure". */
  variety: { id: number; name: string | null; isPlaceholder: boolean };
  /** True while its public page is on. */
  isPublic: boolean;
}

/** One variety the kept plant may end up with. */
export interface MergeVarietyOption {
  /** The variety's id. */
  varietyId: number;
  /** Its name; null for the placeholder, which the app says as "Not sure". */
  name: string | null;
  /** True for the placeholder. */
  isPlaceholder: boolean;
}

/** Which variety the kept plant has, and whether the gardener is asked. */
export interface MergeVarietyChoice {
  /** False when both plants share one variety: nothing to ask. */
  asked: boolean;
  /** The variety to pre-select (and to send when nothing was asked). */
  suggested: number;
  /** The rows to show; empty when not asked. */
  options: MergeVarietyOption[];
}

/** What would move, finished by the server. */
export interface MergeMoves {
  /** How many of each kind move. */
  counts: Record<string, number>;
  /** True when the gone plant's public page would close. */
  publicCloses: boolean;
  /** The lines to show ("1 photo", "2 notes"), in the reader's language. */
  lines: string[];
}

/** The answer of GET /plants/:id/merge-preview. */
export interface MergePreview {
  /** The server's flag. */
  success: boolean;
  /** The crop in running text, plural ("carrots"). */
  crops: string;
  /** The plant that goes. */
  gone: MergePreviewPlant;
  /** The plant that stays. */
  keep: MergePreviewPlant;
  /** The kept plant's variety question. */
  varietyChoice: MergeVarietyChoice;
  /** What moves across. */
  moves: MergeMoves;
  /** True when nothing listed moves and no public page closes. */
  empty: boolean;
}

/** The answer of POST /plants/:id/merge. */
export interface MergeResult {
  /** The server's flag. */
  success: boolean;
  /** The plant that stays. */
  keptPlantObjId: number;
  /** The plant that went. */
  mergedPlantObjId: number;
  /** How many rows moved, by kind. */
  moved: Record<string, number>;
}

/**
 * Reads what merging one plant into another would move, in the app's language.
 *
 * @param plantObjId - The plant that goes.
 * @param into - The plant that stays.
 * @returns The preview.
 */
export async function getMergePreview(plantObjId: number, into: number): Promise<MergePreview> {
  const response = await apiClient.get<MergePreview>(`/plants/${plantObjId}/merge-preview`, {
    params: { into, lang: getLanguage() },
  });
  return response.data;
}

/**
 * Merges a plant into another: its photos, notes and history move across and
 * its card goes. Nothing is deleted and it cannot be undone.
 *
 * @param plantObjId - The plant that goes.
 * @param into - The plant that stays.
 * @param varietyId - The variety the kept plant has afterwards (always sent).
 * @returns The ids and the counts moved.
 */
export async function mergePlant(plantObjId: number, into: number, varietyId: number): Promise<MergeResult> {
  const response = await apiClient.post<MergeResult>(`/plants/${plantObjId}/merge`, {
    intoPlantObjId: into,
    varietyId,
  });
  return response.data;
}
